import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Estate } from '@/features/estates/api/estates';
import useResizeObserver from '@/hooks/use-resize-observer';
import { cn } from '@/lib/utils';

import { CollapsableCard } from './collapsable-card';
import { ObjectRelationSettingsContent } from './object-relation-settings-content';

interface ObjectRelationSettingsProps {
  onChangeEstateId: (id: string) => void;
  disableSelectObject: boolean;
}

export const ObjectRelationSettings = ({
  onChangeEstateId,
  disableSelectObject,
}: ObjectRelationSettingsProps) => {
  const { t } = useTranslation();
  const isMobileView = !useResizeObserver(640);

  const [isOpened, setIsOpened] = useState(false);
  const [selectedEstate, setSelectedEstate] = useState<Estate | undefined>();

  const handleOpenChange = (open: boolean) => {
    if (!disableSelectObject) {
      setIsOpened(open);
    }
  };

  return (
    <CollapsableCard title={t('object')}>
      <Popover onOpenChange={handleOpenChange} open={isOpened}>
        <PopoverTrigger asChild>
          <div
            className={cn(
              'flex min-h-[40px] w-full items-center justify-between gap-x-4 rounded-sm px-4 py-2 sm:min-h-[26px] sm:px-2 sm:py-1 lg:min-h-[40px] lg:px-4 lg:py-2',
              disableSelectObject ? 'bg-[#DADADA]' : 'cursor-pointer bg-[#EFF4F5]'
            )}
          >
            {selectedEstate ? (
              <span className="flex-1 text-left text-sm font-semibold">
                {selectedEstate.name ?? selectedEstate.address?.address}
              </span>
            ) : (
              <span className="flex-1 text-left text-sm font-semibold">{t('select')}*</span>
            )}
          </div>
        </PopoverTrigger>
        {isMobileView ? <PopoverAnchor className="fixed top-0" /> : null}
        <PopoverContent
          className="max-h-screen w-screen overflow-hidden rounded-sm p-0 sm:max-w-[476px]"
          side={isMobileView ? 'bottom' : 'right'}
          sideOffset={isMobileView ? 0 : 10}
        >
          <ObjectRelationSettingsContent
            setSelectedEstate={setSelectedEstate}
            onChangeEstateId={onChangeEstateId}
            closePopover={() => setIsOpened(false)}
          />
        </PopoverContent>
      </Popover>
    </CollapsableCard>
  );
};
